import { getFunctions, httpsCallable, Functions, HttpsCallable } from 'firebase/functions';
import { app } from '../firebase/firebase';
import { expireSessionAndGoToLogin, isSessionExpiredError } from './sessionExpiry';

/**
 * Cloud Functions regions used in the application
 */
export const FUNCTION_REGIONS = {
  /** Default region for most functions (europe-west2) */
  DEFAULT: 'europe-west2',
  /** Region for notification functions (europe-west1) */
  NOTIFICATIONS: 'europe-west1',
} as const;

type FunctionRegion = typeof FUNCTION_REGIONS[keyof typeof FUNCTION_REGIONS];

/**
 * Get a Cloud Functions instance for the specified region
 * @param region - The region to use (defaults to DEFAULT)
 * @returns Firebase Functions instance
 */
export const getCloudFunctions = (region: FunctionRegion = FUNCTION_REGIONS.DEFAULT): Functions => {
  return getFunctions(app, region);
};

/**
 * Get a callable Cloud Function
 * @param functionName - Name of the Cloud Function
 * @param region - Region where the function is deployed (defaults to DEFAULT)
 * @returns Callable function reference
 * 
 * @example
 * ```ts
 * const sendEmail = getCloudFunction('sendEmail');
 * const result = await sendEmail({ to: 'user@example.com' });
 * ```
 */
/** Video publishes wait on Bluesky or LinkedIn processing. Default callable timeout is 70s. */
export const SOCIAL_PUBLISH_TIMEOUT_MS = 300_000;

export function isPublishDeadlineError(err: unknown): boolean {
  const code = err && typeof err === 'object' && 'code' in err ? String((err as { code: unknown }).code) : '';
  const message = err instanceof Error ? err.message : '';
  return code.includes('deadline-exceeded') || /deadline|timed out|timeout/i.test(message);
}

export const getCloudFunction = <T = any, R = any>(
  functionName: string,
  region: FunctionRegion = FUNCTION_REGIONS.DEFAULT,
  options?: { timeout?: number }
): HttpsCallable<T, R> => {
  const functions = getCloudFunctions(region);
  const callable = httpsCallable<T, R>(
    functions,
    functionName,
    options?.timeout ? { timeout: options.timeout } : undefined
  );
  const wrapped = ((data) =>
    callable(data).catch(async (err: unknown) => {
      if (isSessionExpiredError(err)) {
        await expireSessionAndGoToLogin();
      }
      throw err;
    })) as HttpsCallable<T, R>;
  wrapped.stream = callable.stream;
  return wrapped;
};

/**
 * Helper to get notification-related Cloud Functions
 * These functions are deployed in the NOTIFICATIONS region
 */
export const getNotificationFunction = <T = any, R = any>(
  functionName: string
): HttpsCallable<T, R> => {
  return getCloudFunction<T, R>(functionName, FUNCTION_REGIONS.NOTIFICATIONS);
};

